import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { convertImage, MediaApiError } from '../api/mediaClient';
import { ImageFormat } from '../types/media';

export interface FileMeta {
  fileName: string;
  fileSize: number;
  fileType: string;
  lastModified: number;
}

export interface ConversionState {
  fileMeta: FileMeta | null;
  fromFormat: ImageFormat | null;
  toFormat: ImageFormat | null;
  loading: boolean;
  progress: number;
  error: string | null;
  resultUrl: string | null;
  resultFileName: string | null;
  resultBlob: Blob | null;
}

const initialState: ConversionState = {
  fileMeta: null,
  fromFormat: null,
  toFormat: null,
  loading: false,
  progress: 0,
  error: null,
  resultUrl: null,
  resultFileName: null,
  resultBlob: null,
};

export const runConversion = createAsyncThunk(
  'conversion/run',
  async (
    { file, fromFormat, toFormat }: { file: File; fromFormat: ImageFormat; toFormat: ImageFormat },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const result = await convertImage(file, fromFormat, toFormat, (percent) => {
        dispatch(setProgress(percent));
      });

      if (result.blob) {
        const url = URL.createObjectURL(result.blob);
        return {
          resultUrl: url,
          resultFileName: result.fileName || `converted.${toFormat}`,
          resultBlob: result.blob,
        };
      }

      if (result.dataUrl) {
        return {
          resultUrl: result.dataUrl,
          resultFileName: result.fileName || `converted.${toFormat}`,
          resultBlob: null,
        };
      }

      if (result.downloadUrl) {
        return {
          resultUrl: result.downloadUrl,
          resultFileName: result.fileName || `converted.${toFormat}`,
          resultBlob: null,
        };
      }

      return rejectWithValue('No conversion result returned from server.');
    } catch (err) {
      if (err instanceof MediaApiError) {
        return rejectWithValue(err.message);
      }
      if (err instanceof Error) {
        return rejectWithValue(err.message);
      }
      return rejectWithValue('Conversion failed. Please try again.');
    }
  },
);

const conversionSlice = createSlice({
  name: 'conversion',
  initialState,
  reducers: {
    setFileMeta(state, action: PayloadAction<FileMeta | null>) {
      state.fileMeta = action.payload;
      state.error = null;
      state.resultUrl = null;
      state.resultFileName = null;
      state.resultBlob = null;
    },
    setFormats(state, action: PayloadAction<{ from: ImageFormat; to: ImageFormat }>) {
      state.fromFormat = action.payload.from;
      state.toFormat = action.payload.to;
    },
    setProgress(state, action: PayloadAction<number>) {
      state.progress = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    clearResult(state) {
      if (state.resultUrl && state.resultBlob) {
        URL.revokeObjectURL(state.resultUrl);
      }
      state.resultUrl = null;
      state.resultFileName = null;
      state.resultBlob = null;
    },
    resetConversion(state) {
      if (state.resultUrl && state.resultBlob) {
        URL.revokeObjectURL(state.resultUrl);
      }
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(runConversion.pending, (state) => {
        state.loading = true;
        state.progress = 0;
        state.error = null;
        if (state.resultUrl && state.resultBlob) {
          URL.revokeObjectURL(state.resultUrl);
        }
        state.resultUrl = null;
        state.resultFileName = null;
        state.resultBlob = null;
      })
      .addCase(runConversion.fulfilled, (state, action) => {
        state.loading = false;
        state.progress = 100;
        state.resultUrl = action.payload.resultUrl;
        state.resultFileName = action.payload.resultFileName;
        state.resultBlob = action.payload.resultBlob;
      })
      .addCase(runConversion.rejected, (state, action) => {
        state.loading = false;
        state.progress = 0;
        state.error = (action.payload as string) || 'Conversion failed.';
      });
  },
});

export const { setFileMeta, setFormats, setProgress, clearError, clearResult, resetConversion } =
  conversionSlice.actions;

export default conversionSlice.reducer;
